import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What can I try when antidepressants don't work?",
    answer:
      "If antidepressants have not produced adequate improvement, options include medication optimization or augmentation, psychotherapy, conventional TMS, accelerated TMS (SAINT®), Spravato (esketamine), and ketamine. A psychiatric evaluation reviews whether past trials were adequate, screens for misdiagnosis (such as bipolar disorder), and recommends the most appropriate next step.",
  },
  {
    question: "Does 'not working' mean my depression is treatment-resistant?",
    answer:
      "Not necessarily. A medication stopped early because of side effects, or never increased beyond the starting dose, is usually considered an incomplete trial rather than a failed one. Treatment-resistant depression generally requires at least two adequate trials — therapeutic dose for about six to eight weeks. A psychiatrist can review your history.",
  },
  {
    question: "Should I stop taking my antidepressant if it's not working?",
    answer:
      "No. Stopping abruptly can cause discontinuation symptoms and raises the risk of relapse. If you want to reduce or stop medication, ask your prescriber to plan a gradual taper. Many treatments — including TMS and psychotherapy — are delivered alongside medication, not instead of it.",
  },
  {
    question: "Can TMS help if antidepressants haven't worked?",
    answer:
      "Yes. Conventional TMS is FDA-cleared specifically for adults with major depressive disorder who have not improved on antidepressant medication. SAINT® is an accelerated version. Both require a psychiatric evaluation to confirm eligibility.",
  },
  {
    question:
      "What is the difference between inadequate response and treatment-resistant depression?",
    answer:
      "Inadequate response means a treatment has not produced enough improvement. Treatment-resistant depression is a clinical term for depression that has not responded to at least two adequate antidepressant trials. Inadequate response is a reason to reassess; TRD is a pattern that guides the next steps.",
  },
];

export const pageAntidepressantsDontWork: LearnPageData = {
  slug: "when-antidepressants-dont-work",
  title: "When Antidepressants Don't Work: What Next? | Neuro Wellness",
  description:
    "What to try when antidepressants aren't working: inadequate response, treatment-resistant depression, medication adjustments, psychotherapy, TMS, SAINT®, Spravato, and other options.",
  canonical: "/learn/when-antidepressants-dont-work",
  h1: "What Can You Try When Antidepressants Aren't Working?",
  breadcrumbLabel: "When Antidepressants Don't Work",
  crumbs: baseCrumbs("When Antidepressants Don't Work"),
  shortAnswer:
    "If antidepressants have not produced adequate improvement, you are not out of options. Inadequate response to medication is common — roughly one-third of people do not reach remission after two treatment steps. Options include medication optimization or augmentation, psychotherapy, conventional TMS, accelerated TMS (SAINT®), Spravato (esketamine), and ketamine. A psychiatric evaluation reviews whether past trials were adequate, screens for misdiagnosis (such as bipolar disorder), and recommends the most appropriate next step. Do not stop medication on your own.",
  keyTakeaways: [
    "Inadequate response to antidepressants is common.",
    "Options include medication changes, therapy, TMS, SAINT®, Spravato, and ketamine.",
    "A psychiatric evaluation reviews whether past trials were adequate.",
    "Misdiagnosis — such as bipolar disorder — can mimic treatment resistance.",
    "Do not stop medication without consulting your prescriber.",
  ],

  sections: [
    {
      heading: "Understanding Inadequate Response",
      body: (
        <>
          {P(
            "Inadequate response means a treatment has not produced enough improvement. It is common: in the STAR*D study, roughly one-third of people did not reach remission after two treatment steps. Inadequate response is a reason to reassess, not a reason to give up.",
          )}
          {P(
            <>
              An inadequate response may be due to an incomplete trial (too low
              a dose or too short a duration), a missed diagnosis, or genuine
              treatment resistance. Learn more about{" "}
              <a
                href="/learn/what-is-treatment-resistant-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                treatment-resistant depression
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Treatment-Resistant Depression",
      body: (
        <>
          {P(
            "Treatment-resistant depression (TRD) is major depressive disorder that has not responded adequately to at least two antidepressant trials of adequate dose and duration in the current episode. It describes treatment history, not a separate diagnosis. It does not mean the depression cannot be treated.",
          )}
          {P(
            "A psychiatrist reviews your medication history — doses, durations, side effects — to determine whether past trials were adequate before labeling depression as treatment-resistant.",
          )}
        </>
      ),
    },
    {
      heading: "Medication Adjustments",
      body: (
        <>
          {P(
            "If current medication is not working, a psychiatrist may recommend optimizing the dose, switching to a different antidepressant class, or adding an augmentation medication. These changes can take four to eight weeks to assess. Medication remains a first-line option and may be combined with other treatments.",
          )}
          {P(
            "Do not stop or change medication on your own — abrupt discontinuation can cause withdrawal symptoms and relapse.",
          )}
        </>
      ),
    },
    {
      heading: "Psychotherapy",
      body: (
        <>
          {P(
            "Evidence-based psychotherapies — CBT, behavioral activation, interpersonal therapy — can help when medication alone has not. Therapy addresses patterns of thought and behavior that medication does not. It is often combined with medication or brain stimulation.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/services/psychotherapy"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                psychotherapy
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "TMS and Accelerated TMS",
      body: (
        <>
          {P(
            "Conventional TMS is FDA-cleared for adults with major depressive disorder who have not improved on antidepressant medication. It is delivered over about six weeks and is broadly covered by insurance. SAINT® is an accelerated version — five days with fMRI-guided targeting on the Magnus Neuromodulation System — typically self-pay.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-tms-therapy-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                TMS therapy
              </a>{" "}
              and{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT®
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Spravato (Esketamine)",
      body: (
        <>
          {P(
            "Spravato is an FDA-approved nasal spray for treatment-resistant depression, administered in a certified clinic with monitoring. It may act faster than traditional oral antidepressants. Coverage varies by plan; prior authorization is common.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/services/spravato"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Spravato
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Other Clinician-Directed Options",
      body: (
        <>
          {P(
            "Other options a psychiatrist may discuss include ketamine infusions (off-label), ECT for severe or psychotic depression, and combinations of the above. The right option depends on your diagnosis, history, eligibility, and preferences.",
          )}
          {P(
            "If you are having thoughts of self-harm, call or text 988 (Suicide & Crisis Lifeline) in the U.S., or go to the nearest emergency room.",
          )}
        </>
      ),
    },
    {
      heading: "Discuss With Your Treating Clinician",
      body: (
        <>
          {P(
            "The most important step is a full re-evaluation with a psychiatrist. This should include reviewing whether past trials were adequate, screening for bipolar disorder and other comorbidities, and a medical workup. Do not stop current medication on your own.",
          )}
          {P(
            <>
              Neuro Wellness offers TMS, SAINT®, Spravato, ketamine,
              psychotherapy, and medication management in Coral Springs,
              Florida.{" "}
              <a
                href="/contactus"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Request a consultation
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. Spravato (esketamine) prescribing information.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/when-antidepressants-dont-work#webpage`,
        name: "What Can You Try When Antidepressants Aren't Working?",
        url: `${SITE_URL}/learn/when-antidepressants-dont-work`,
        description:
          "Options when antidepressants don't work: inadequate response, TRD, medication adjustments, therapy, TMS, SAINT®, Spravato, and ketamine.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/when-antidepressants-dont-work#faq`),
      breadcrumbJsonld(baseCrumbs("When Antidepressants Don't Work")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "Treatment-Resistant Depression",
      href: "/learn/what-is-treatment-resistant-depression",
      description: "What TRD is and the options",
    },
    {
      title: "Depression Treatment Beyond More Medication",
      href: "/learn/depression-treatment-without-more-medication",
      description: "Non-medication treatment options",
    },
  ],
};
